import { z as zod } from 'zod/v3';
import { Erod } from '@cotera/era';

const { Schema: z } = Erod;

export const apiKeySecret = z.object({
  type: z.literal('apiKey'),
  apiKey: z.string(),
});

export type ApiKeySecret = Erod.Infer<typeof apiKeySecret>;

export const oauthSecret = z.object({
  type: z.literal('oauth'),
  clientId: z.string(),
  clientSecret: z.string(),
  subdomain: z
    .string()
    .nullable()
    .default(null)
    .describe('The subdomain of the API host, if user/org specific.'),
  refreshToken: z.string().nullable().default(null),
  scopes: z.array(z.string()).nullable().default(null),
});

export type OauthSecret = Erod.Infer<typeof oauthSecret>;

export const passwordSecret = z.object({
  type: z.literal('password'),
  username: z.string(),
  password: z.string(),
});

export type PasswordSecret = Erod.Infer<typeof passwordSecret>;

export const noAuthSecret = z.object({
  type: z.literal('noAuth'),
});

export type NoAuthSecret = Erod.Infer<typeof noAuthSecret>;

export type ToolSecret =
  | PasswordSecret
  | OauthSecret
  | NoAuthSecret
  | ApiKeySecret;

export const ToolSecretZodSchema: zod.ZodSchema<ToolSecret> = zod.union([
  apiKeySecret.zodSchema({ strict: true }),
  passwordSecret.zodSchema({ strict: true }),
  oauthSecret.zodSchema({ strict: true }),
  noAuthSecret.zodSchema({ strict: true }),
]);

export const TOOL_ERRORS = z.object({
  errorType: z.enum([
    'ToolNotFound',
    'ToolCredentialsFailure',
    'ToolInputValidationError',
    'ToolOutputValidationError',
    'ToolCallFailed',
    'ToolCallTransientError',
  ]),
  toolName: z.string(),
  message: z.string(),
});

export type ToolError = Erod.Infer<typeof TOOL_ERRORS>;

export const keyValuePairs = z.array(
  z.object({
    key: z.string(),
    value: z.string(),
  })
);

export type GenericToolOutput = {
  readonly output: unknown;
  readonly usage: Record<string, number>;
};

export type ProviderMeta = {
  displayName: string;
  description: string;
  logo?: string;
  icon?: string;
  schema?: Erod.Def<any>;
  type: ToolSecret['type'];
};

export type ToolMeta = {
  displayName: string;
  description: string;
  icon?: string;
};
